import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem, Tooltip, Divider } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n, { isRTL } from './i18n';
import MenuIcon from '@mui/icons-material/Menu';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { CoinsProvider } from './contexts/CoinsContext';
import { BlockchainProvider } from './contexts/BlockchainProvider';
import { ComplianceProvider, useCompliance } from './contexts/ComplianceContext';
import { useWalletSync } from './hooks/useWalletSync';
import { usePrices } from './hooks/usePrices';
import { useLanguageNavigation } from './hooks/useLanguageNavigation';
import iconService from './services/iconService';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { getNotifications, markNotificationsRead, deleteNotification, getAllNotifications } from './services/api';
import api from './services/api';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { messaging, getToken, onMessage } from './firebase';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

// SEO and Analytics Components
import SEO from './components/SEO';
import GoogleAnalytics from './components/GoogleAnalytics';

// Components
import Login from './components/Login';
import Footer from './components/Footer';
import LanguageAwareRoutes from './components/LanguageAwareRoutes';
import PortfolioPage from './PortfolioPage';
import VPortfolioPage from './pages/VPortfolioPage';
import SettingsPage from './SettingsPage';
import CoinPage from './pages/CoinPage';
import ExitStrategyPage from './pages/ExitStrategyPage';
import AdminPage from './pages/AdminPage';
import MobileDrawer from './components/MobileDrawer';
import SyncProgress from './components/SyncProgress';
import VOrdersPage from './pages/VOrdersPage';
import VLeaderboardPage from './pages/VLeaderboardPage';
import WalletTransactionsPage from './pages/WalletTransactionsPage';
import ManualHoldingsPage from './pages/ManualHoldingsPage';
import WalletsPage from './pages/WalletsPage';
import AboutPage from './pages/AboutPage';
import AddHoldingsPage from './pages/AddHoldingsPage';
import ComplianceWrapper from './components/ComplianceWrapper';
import ErrorBoundary from './components/ErrorBoundary';

function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [allModalOpen, setAllModalOpen] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const { currentUser } = useAuth();
  const { navigateWithLang } = useLanguageNavigation();
  const { t } = useTranslation();

  const fetchNotifications = async () => {
    try {
      const res = await getNotifications();
      const unread = (res.notifications || []).filter(n => !n.read_at);
      setNotifications(unread);
      setUnreadCount(unread.length);
    } catch (e) {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const fetchAllNotifications = async () => {
    try {
      const res = await getAllNotifications();
      setAllNotifications(res.notifications || []);
    } catch (e) {
      setAllNotifications([]);
    }
  };

  useEffect(() => {
    if (currentUser) fetchNotifications();
    // Listen for FCM messages
    const unsubscribe = messaging && onMessage ? onMessage(messaging, (payload) => {
      // console.log('FCM onMessage received:', payload);
      fetchNotifications();
      if (payload?.notification) {
        // Show a system notification even in foreground
        if (window.Notification && Notification.permission === 'granted') {
          new window.Notification(payload.notification.title, {
            body: payload.notification.body,
            icon: '/logo192.png',
          });
        }
      }
    }) : null;
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const handleRead = async (id) => {
    await deleteNotification(id);
    fetchNotifications();
    // Log after reading a notification
    // console.log('[NotificationBell] Notification read:', id);
    if (notifications.length === 1) {
      // console.log('[NotificationBell] This was the last unread notification, closing popover.');
      handleClose();
    }
  };

  const handleOpenAll = async () => {
    setAllModalOpen(true);
    fetchAllNotifications();
  };
  const handleCloseAll = () => setAllModalOpen(false);

  // New: handle click on notification to open exit strategy (do not mark as read)
  const handleNotificationClick = (notification) => {
    // Try to get coin symbol from notification data
    let symbol = null;
    if (notification.data) {
      try {
        const data = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
        if (data && data.triggeredSteps && data.triggeredSteps.length > 0) {
          symbol = data.triggeredSteps[0].symbol;
        }
      } catch (e) {}
    }
    if (!symbol) {
      // Fallback: try to parse from message
      const match = /for ([A-Z0-9]+)/.exec(notification.message);
      if (match) symbol = match[1];
    }
    if (symbol) {
      navigateWithLang(`exit-strategy/${symbol}`);
      handleClose();
    }
  };

  // Debug: Close popover on blur or scroll
  useEffect(() => {
    if (!open) return;
    const handleBlur = (e) => {
      // console.log('[NotificationBell] Popover blur event, closing popover.');
      handleClose();
    };
    const handleScroll = (e) => {
      // console.log('[NotificationBell] Scroll event detected, closing popover.');
      handleClose();
    };
    window.addEventListener('blur', handleBlur);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  // Close modal when last unread notification is deleted
  useEffect(() => {
    if (allModalOpen && allNotifications.length > 0 && allNotifications.every(n => n.read_at)) {
      setAllModalOpen(false);
    }
  }, [allNotifications, allModalOpen]);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} sx={{ ml: 1 }}>
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 320, maxWidth: 400 } }}
      >
        <Box sx={{ p: 2, minWidth: 320 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <NotificationsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('notifications.title')}</Typography>
          </Box>
          <List dense>
            {notifications.length === 0 && (
              <ListItem><ListItemText primary={t('notifications.noUnread')} /></ListItem>
            )}
            {notifications.map((n) => (
              <ListItem key={n.id} alignItems="flex-start" sx={{ bgcolor: 'rgba(25,118,210,0.08)', cursor: 'pointer' }}
                onClick={() => handleNotificationClick(n)}
                secondaryAction={
                  <IconButton edge="end" aria-label="mark as read" onClick={e => { e.stopPropagation(); handleRead(n.id); }}>
                    <CheckIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      <span style={{ color: 'rgba(0,0,0,0.6)' }}>{n.message}</span>
                      <br />
                      <Typography variant="caption" color="text.secondary" component="span">
                        {new Date(n.created_at).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Button onClick={handleOpenAll} fullWidth sx={{ mt: 1 }}>{t('notifications.viewAll')}</Button>
        </Box>
      </Popover>
      <Dialog open={allModalOpen} onClose={handleCloseAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsIcon sx={{ mr: 1 }} />
            {t('notifications.all')}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {allNotifications.length === 0 && (
              <ListItem><ListItemText primary={t('notifications.noNotifications')} /></ListItem>
            )}
            {allNotifications.map((n) => (
              <ListItem key={n.id} alignItems="flex-start" sx={{ bgcolor: !n.read_at ? 'rgba(25,118,210,0.08)' : undefined }}>
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      <span style={{ color: 'rgba(0,0,0,0.6)' }}>{n.message}</span>
                      <br />
                      <Typography variant="caption" color="text.secondary" component="span">
                        {new Date(n.created_at).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
                {n.read_at ? <CheckIcon fontSize="small" color="disabled" sx={{ ml: 1 }} /> : null}
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAll} startIcon={<CloseIcon />}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function AppBarUserMenu() {
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    await logout();
    handleClose();
  };

  if (!currentUser) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
      {!isMobile && (
        <Typography variant="body1" sx={{ color: 'white', mr: 2 }}>
          {currentUser.displayName || currentUser.email}
        </Typography>
      )}
      
      <Tooltip title={currentUser.email}>
        <IconButton onClick={handleMenu} size="small" sx={{ ml: 1 }}>
          <Avatar src={currentUser.photoURL} alt={currentUser.displayName || currentUser.email} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Divider />
        <MenuItem onClick={handleLogout}>{t('common.logout')}</MenuItem>
      </Menu>
    </Box>
  );
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return currentUser ? children : <Navigate to="/login" />;
};

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [showAppBar, setShowAppBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { navigateWithLang } = useLanguageNavigation();

  // Configure status bar for iOS
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Light });
      StatusBar.setBackgroundColor({ color: '#1976d2' }); // Match Material-UI primary color
      StatusBar.setOverlaysWebView({ overlay: false }); // Don't overlay, let AppBar handle safe area
    }
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };



  const rtl = isRTL();

  // Margin only for desktop (not mobile)
  const mainContentMargin = !isMobile
    ? rtl
      ? { mr: { sm: '250px', xs: 0 } }
      : { ml: { sm: '250px', xs: 0 } }
    : {};

  useEffect(() => {
    document.dir = rtl ? 'rtl' : 'ltr';
  }, [rtl]);

  // Initialize icon service when app starts
  useEffect(() => {
    if (currentUser) {
      // console.log('[App] Initializing icon service...');
      iconService.loadAllIcons().catch(err => {
        console.warn('[App] Error initializing icons:', err);
      });
    }
  }, [currentUser]);

  // FCM token registration
  useEffect(() => {
    if (!currentUser) return;
    // Request notification permission and get FCM token
    const registerFcmToken = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const token = messaging && getToken ? await getToken(messaging, { vapidKey: 'BK2iJy8r86-qvUPti_skkDYB1FfRdpfD01GgSVKqQczZGB9w62sNtZ8o6Vfts55OmDHQaJN60qRkpkSVjNWsKps' }) : null;
          if (token) {
            // Send token to backend using the api service
            await api.post('/api/users/fcm-token', { fcmToken: token });
          }
        } else if (permission === 'denied') {
          alert('Notification permission denied. You will not receive push notifications.');
        }
      } catch (err) {
        console.error('Error getting FCM token:', err);
      }
    };
    registerFcmToken();
  }, [currentUser]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          if (currentScrollY < 10) {
            setShowAppBar(true);
          } else if (currentScrollY > lastScrollY) {
            setShowAppBar(false); // scrolling down
          } else {
            setShowAppBar(true); // scrolling up
          }
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {/* SEO and Analytics Components */}
        <SEO 
          title="Tulidu - Crypto Portfolio Tracker"
          description="Track and manage your cryptocurrency portfolio with real-time prices, portfolio analytics, and comprehensive crypto tracking tools."
          keywords="cryptocurrency, portfolio tracker, crypto tracker, bitcoin, ethereum, blockchain, crypto analytics"
          url="https://tulidu-wallet.com"
        />
        <GoogleAnalytics />
        <Box dir={rtl ? 'rtl' : 'ltr'} sx={{ 
          display: 'flex', 
          bgcolor: 'background.default',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <AppBar 
            position="fixed" 
            sx={{ 
              zIndex: (theme) => theme.zIndex.drawer + 1,
              transition: 'top 0.3s',
              top: showAppBar ? 0 : '-70px',
              // Add safe area insets for iOS - extend behind status bar
              paddingTop: Capacitor.isNativePlatform() ? 'env(safe-area-inset-top)' : 0,
              minHeight: Capacitor.isNativePlatform() ? 'calc(56px + env(safe-area-inset-top))' : '56px',
            }}
          >
            <Toolbar>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                {rtl ? (
                  <>
                    {/* Right: menu icon (if mobile), then Tulidu */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isMobile && (
                        <IconButton
                          color="inherit"
                          aria-label="open drawer"
                          edge="end"
                          onClick={handleDrawerToggle}
                          sx={{ pl: 2 }}
                        >
                          <MenuIcon />
                        </IconButton>
                      )}
                      <Box
                        onClick={() => navigateWithLang('portfolio')}
                        sx={{ cursor: 'pointer', display: 'inline-block' }}
                      >
                        <Typography
                          variant="h6"
                          noWrap
                          component="div"
                          sx={{ color: 'white', textAlign: 'right', ml: isMobile ? 1 : 0 }}
                        >
                          Tulidu
                        </Typography>
                      </Box>
                    </Box>
                    {/* Left: avatar (most left), then notification */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row-reverse' }}>
                      <AppBarUserMenu />
                      {currentUser && <NotificationBell />}
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Left: menu icon (if mobile), then Tulidu */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isMobile && (
                        <IconButton
                          color="inherit"
                          aria-label="open drawer"
                          edge="start"
                          onClick={handleDrawerToggle}
                        >
                          <MenuIcon />
                        </IconButton>
                      )}
                      <Box
                        onClick={() => navigateWithLang('portfolio')}
                        sx={{ cursor: 'pointer', display: 'inline-block', ml: isMobile ? 1 : 0 }}
                      >
                        <Typography
                          variant="h6"
                          noWrap
                          component="div"
                          sx={{ color: 'white', textAlign: 'left' }}
                        >
                          Tulidu
                        </Typography>
                      </Box>
                    </Box>
                    {/* Right: avatar (most right), then notification */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AppBarUserMenu />
                      {currentUser && <NotificationBell />}
                    </Box>
                  </>
                )}
              </Box>
            </Toolbar>
          </AppBar>
          <MobileDrawer open={drawerOpen} onClose={handleDrawerToggle} anchor={rtl ? 'right' : 'left'} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 0, sm: 2, md: 2, lg: 3 },
              width: { sm: `calc(100% - 250px)` },
              mt: Capacitor.isNativePlatform() ? 'calc(64px + env(safe-area-inset-top))' : '64px',
              bgcolor: 'background.default',
              maxWidth: '100%',
              overflow: 'hidden',
              ...mainContentMargin,
              display: 'flex',
              flexDirection: 'column',
              minHeight: Capacitor.isNativePlatform() ? 'calc(100vh - 64px - env(safe-area-inset-top))' : 'calc(100vh - 64px)',
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <ErrorBoundary>
                <ComplianceWrapper>
                  <LanguageAwareRoutes />
                </ComplianceWrapper>
              </ErrorBoundary>
            </Box>
            <Footer />
          </Box>
        </Box>
        {currentUser && <SyncProgress />}
      </ThemeProvider>
    </I18nextProvider>
  );
}

// Root App with Providers
function AppWithProviders() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <CoinsProvider>
          <BlockchainProvider>
            <ComplianceProvider>
              <App />
            </ComplianceProvider>
          </BlockchainProvider>
        </CoinsProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default AppWithProviders;
